import {
  Button,
  Flex,
  Heading,
  Icon,
  Skeleton,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import React from "react";
import { RiAlertLine, RiRotateLockLine } from "react-icons/ri";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import * as FSQ from "src/generated/hooks/form-service/queries";
import {
  rotateApiCredentialSecret,
  updateApiCredential,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import {
  ApiCredentialFormSchema,
  ApiCredentialFormType,
} from "src/schemas/ApiCredential";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import { isNonEmptyString } from "src/services/predicates";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { ApiCredentialForm } from "./ApiCredentialForm";
import {
  ApiCredentialSecretReveal,
  ApiCredentialSecretRevealProps,
} from "./ApiCredentialSecretReveal";

export const EditApiCredential = () => {
  const toast = useAvelaToast();
  const organization = useOrganization();
  const organizationId = organization.toNullable()?.id;
  const { pk } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [secretReveal, setSecretReveal] =
    React.useState<ApiCredentialSecretRevealProps | null>(null);

  const rotateDialog = useConfirmationDialog({
    header: "Rotate client secret?",
    body: (
      <Flex alignItems="center" gap={3}>
        <Icon as={RiAlertLine} color="orange.500" boxSize={10} flexShrink={0} />
        <Text>
          This will break live integrations right away and cannot be undone.
        </Text>
      </Flex>
    ),
    cancelButton: {
      label: "No, cancel",
    },
    confirmButton: {
      label: "Yes, rotate secret",
      colorScheme: "red",
    },
  });

  const credentialQuery = FSQ.useGetApiCredential(
    isNonEmptyString(pk)
      ? { path: { clientId: pk } }
      : { path: { clientId: "" } },
    [pk],
    { enabled: isNonEmptyString(pk) && organization.hasData() }
  );

  const userGroupsQuery = FSQ.useListUserGroups({}, [], {
    enabled: organization.hasData(),
  });

  const teamsQuery = FSQ.useListTeams(
    organization.hasData()
      ? { query: { organizationId: organization.data.id } }
      : { query: { organizationId: "" } },
    [organizationId],
    { enabled: organization.hasData() }
  );

  const remoteData = React.useMemo(
    () =>
      RD.toTuple3(
        RD.fromRestResult(credentialQuery),
        RD.fromRestResult(userGroupsQuery),
        RD.fromRestResult(teamsQuery)
      )
        .map(([credentialResponse, userGroupsResponse, teamsResponse]) => ({
          apiCredential: credentialResponse.data,
          userGroups: userGroupsResponse.data.userGroups,
          teams: teamsResponse.data.teams,
        }))
        .mapError(RD.errorResponseToError)
        .mapError(
          (error) =>
            error ??
            new Error("Failed to load API credential. Please try again.")
        ),
    [credentialQuery, userGroupsQuery, teamsQuery]
  );

  const handleRotateSecret = React.useCallback(
    async (params: {
      clientName: string;
      userGroup?: string;
      teamName?: string;
      allowedIps: string[];
    }) => {
      if (!pk) return;

      const confirmed = await rotateDialog.confirm({
        onConfirm: (helpers) => {
          helpers.setButton({
            isLoading: true,
            loadingText: "Rotating...",
            label: "",
            colorScheme: "red",
          });
        },
      });
      if (!confirmed) return;

      try {
        const { data: rotateResult, error } = await rotateApiCredentialSecret({
          path: { clientId: pk },
        });

        if (!rotateResult) {
          console.warn("Failed to rotate API credential secret.", error);
          throw new Error("Failed to rotate API credential secret.");
        }

        toast.closeAll();
        toast({
          id: "rotate-api-credential-secret",
          title: "Client secret rotated",
        });
        rotateDialog.helpers.close();
        setSecretReveal({
          ...params,
          clientId: rotateResult.data.clientId,
          clientSecret: rotateResult.data.clientSecret,
          notice:
            "Client secrets will only show once here. Please securely save the information below. The previous secret no longer works.",
        });
      } catch {
        toast.closeAll();
        toast.error({
          id: "rotate-api-credential-secret",
          title: "Something went wrong!",
          description: "Check your network and try again.",
          isClosable: true,
        });
        rotateDialog.helpers.close();
      }
    },
    [pk, rotateDialog, toast]
  );

  if (secretReveal) {
    return (
      <Flex direction="column" height="100%" gap={6}>
        <Breadcrumb
          items={breadcrumb.dataServices.getBreadcrumbsForEditApiCredential(
            organization,
            pk ?? ""
          )}
          mb={8}
        />
        <ApiCredentialSecretReveal {...secretReveal} />
        <Spacer />
        <AdminFormButtons>
          <Button
            as={NavLink}
            to={organization
              .map((org) => Url.OrgAdmin.DataServices.index(org))
              .withDefault("#")}
            isLoading={organization.isLoading()}
            variant="outline"
            colorScheme="gray"
          >
            Close
          </Button>
        </AdminFormButtons>
      </Flex>
    );
  }

  return (
    <>
      {rotateDialog.confirmationDialog}
      <GQLRemoteDataView
        remoteData={remoteData}
        loading={<LoadingSkeleton />}
        reloading={null}
        error={() => {
          if (
            credentialQuery.isError &&
            !userGroupsQuery.isError &&
            !teamsQuery.isError
          ) {
            return <NotFound heading="" text="API credential not found" />;
          }

          return (
            <GenericError message="Failed to load API credential. Please try again." />
          );
        }}
      >
        {({ apiCredential, userGroups, teams }) => {
          if (!apiCredential) {
            return <NotFound heading="" text="API credential not found" />;
          }

          const initialValues: ApiCredentialFormType = {
            client_name: apiCredential.clientName,
            client_description: "",
            role: apiCredential.userGroupId,
            team_id: apiCredential.teamId ?? "",
            allowed_ips: apiCredential.allowedIps ?? [],
          };

          const handleSubmit = async (value: ApiCredentialFormType) => {
            try {
              if (!organization.hasData()) throw Error("Invalid organization.");
              if (!pk) throw new Error("Invalid API credential");

              await updateApiCredential({
                path: { clientId: pk },
                body: {
                  clientName: value.client_name,
                  userGroupId: value.role,
                  teamId: isNonEmptyString(value.team_id)
                    ? value.team_id
                    : undefined,
                  allowedIps: value.allowed_ips,
                },
              });

              toast.closeAll();
              toast({
                id: "update-api-credential",
                title: "API credential updated",
              });
              await Promise.all([
                queryClient.invalidateQueries({
                  queryKey: [FSQ.useListApiCredentialsKey],
                }),
                queryClient.invalidateQueries({
                  queryKey: [FSQ.useGetApiCredentialKey],
                }),
              ]);
              navigate(Url.OrgAdmin.DataServices.index(organization.data));
            } catch {
              toast.closeAll();
              toast.error({
                id: "update-api-credential",
                title: `Oops! Something went wrong!`,
                description: "Check your network and try again.",
                isClosable: true,
              });
            }
          };

          return (
            <Flex direction="column" height="100%">
              <Breadcrumb
                items={breadcrumb.dataServices.getBreadcrumbsForEditApiCredential(
                  organization,
                  pk ?? ""
                )}
                mb={8}
              />

              <Formik<ApiCredentialFormType>
                initialValues={initialValues}
                validate={validateWithZod(ApiCredentialFormSchema)}
                onSubmit={handleSubmit}
              >
                {(formik) => (
                  <Flex
                    as={Form}
                    direction="column"
                    gap={6}
                    flexGrow="1"
                    noValidate
                  >
                    <Heading as="h1" variant="admin">
                      Edit API credential
                    </Heading>
                    <ApiCredentialForm userGroups={userGroups} teams={teams} />
                    <Spacer />
                    <AdminFormButtons>
                      <Button
                        as={NavLink}
                        to={organization
                          .map((org) => Url.OrgAdmin.DataServices.index(org))
                          .withDefault("#")}
                        isLoading={organization.isLoading()}
                        isDisabled={formik.isSubmitting}
                        variant="outline"
                        colorScheme="gray"
                      >
                        Cancel
                      </Button>
                      <Spacer />
                      <Button
                        type="button"
                        variant="outline"
                        leftIcon={<Icon as={RiRotateLockLine} />}
                        isDisabled={formik.isSubmitting}
                        onClick={() => {
                          const userGroup = userGroups.find(
                            (group) => group.id === apiCredential.userGroupId
                          );
                          const team = teams.find(
                            (entry) => entry.id === apiCredential.teamId
                          );
                          handleRotateSecret({
                            clientName: apiCredential.clientName,
                            userGroup: userGroup?.label ?? userGroup?.name,
                            teamName: team?.name,
                            allowedIps: apiCredential.allowedIps ?? [],
                          });
                        }}
                        mr={2}
                      >
                        Rotate client secret
                      </Button>
                      <Button
                        type="submit"
                        isLoading={formik.isSubmitting}
                        isDisabled={!formik.dirty || !formik.isValid}
                      >
                        Update
                      </Button>
                    </AdminFormButtons>
                  </Flex>
                )}
              </Formik>
            </Flex>
          );
        }}
      </GQLRemoteDataView>
    </>
  );
};

const LoadingSkeleton = () => {
  return (
    <Flex direction="column" gap={6}>
      <Skeleton height="2rem" width="20rem" />
      <Skeleton height="2.5rem" width="100%" />
      <Skeleton height="2.5rem" width="100%" />
      <Skeleton height="2.5rem" width="50%" />
      <Skeleton height="3rem" width="10rem" alignSelf="flex-end" />
    </Flex>
  );
};
